import React from 'react';
import { FastField, FastFieldProps, FormikProps } from 'formik';
import { MobXProviderContext } from 'mobx-react';

import {
  Button,
  DateTimeRanges,
  FormGroup,
  SelectAdv,
  Switch,
} from '@lib/components';
import { FreeItemPromo } from '../promos/type';
import { getPaymentMethods } from '../promos/util';
import { ListOrderTimesOptions, ListServicesOptions } from '@lib/common';

interface Props {
  form: FormikProps<FreeItemPromo>;
  getFieldError: (
    form: FormikProps<FreeItemPromo>,
    field: string
  ) => T.ObjectAny | string | undefined | null;
}

export const LimitsTab = ({ form, getFieldError }: Props) => {
  const { store } = React.useContext(MobXProviderContext);
  const restaurant = store.restaurant;
  const paymentMethods = getPaymentMethods(restaurant);

  return (
    <div className="p-4">
      <FastField name="restrictions.service_type">
        {({ field }: FastFieldProps) => (
          <FormGroup
            title="Services"
            help="Select which services this promo will be valid for.Leave empty for it to be valid for all services"
            error={getFieldError(form, 'restrictions.service_type')}
          >
            <SelectAdv
              type="multi"
              value={field.value}
              onChange={(options: string[]) => {
                form.setFieldValue('restrictions.service_type', options);
              }}
              options={ListServicesOptions}
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.payment_methods">
        {({ field }: FastFieldProps) => (
          <FormGroup
            title="Payment Methods"
            help="Select which payment methods this promo will be valid for.Leave empty for it to be valid for all payment methods"
            error={getFieldError(form, 'restrictions.payment_methods')}
          >
            <SelectAdv
              type="multi"
              value={field.value}
              onChange={(options: string[]) =>
                form.setFieldValue('restrictions.payment_methods', options)
              }
              options={paymentMethods}
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.order_times">
        {({ field }: FastFieldProps) => (
          <FormGroup
            title="Order Times"
            help="Select which order times this promo will be valid for,I.e oreders due immediately or pre-orders. Leave empty for it to be valid at all times."
            error={getFieldError(form, 'restrictions.order_times')}
          >
            <SelectAdv
              type="multi"
              value={field.value || []}
              onChange={(v: string[]) =>
                form.setFieldValue('restrictions.order_times', v)
              }
              options={ListOrderTimesOptions}
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.once_per_customer">
        {({ field }: FastFieldProps) => (
          <FormGroup
            title="Once Per Customer"
            help="Allows a customer to only redeem this promotion only once. If a customer is not logged in, it resets if their browser cookies are cleared"
            error={getFieldError(form, 'restrictions.once_per_customer')}
          >
            <Switch
              id="restrictions.once_per_customer"
              checked={field.value}
              onChange={e =>
                form.setFieldValue(
                  'restrictions.once_per_customer',
                  e.target.checked
                )
              }
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.authenticated_user_only">
        {({ field }: FastFieldProps) => (
          <FormGroup
            title="Logged In Customers Only"
            help="This promo can only be redeemed by customers who have created an account and logged in"
            error={getFieldError(form, 'restrictions.authenticated_user_only')}
          >
            <Switch
              id="restrictions.authenticated_user_only"
              checked={field.value}
              onChange={e =>
                form.setFieldValue(
                  'restrictions.authenticated_user_only',
                  e.target.checked
                )
              }
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.valid_datetime_ranges">
        {({ field }: FastFieldProps) => (
          <FormGroup
            optional={true}
            title="Valid Dates / Times"
            help="Set specific date-time ranges when this promotion can be used. Ignore this if the promotion is valid at all times"
            error={getFieldError(form, 'restrictions.valid_datetime_ranges')}
          >
            <Button
              type="button"
              color="primary-inverse"
              size="xs"
              onClick={() => {
                form.setFieldValue('restrictions.valid_datetime_ranges', [
                  ...field.value,
                  { start: '', end: '' },
                ] as T.Core.Business.BusinessDateTimeRanges);
              }}
            >
              Add Date Time Range
            </Button>
            <DateTimeRanges
              formats={restaurant.settings.region.formats}
              locale={restaurant.settings.region.locale}
              timezone={restaurant.settings.region.timezone}
              values={field.value}
              allowNone={true}
              onChange={values =>
                form.setFieldValue('restrictions.valid_datetime_ranges', values)
              }
            />
          </FormGroup>
        )}
      </FastField>

      <FastField name="restrictions.automatically_apply">
        {({ field }: FastFieldProps) => (
          <FormGroup
            optional={true}
            title="Automatically Apply Promotion"
            help="Automatically apply the promotion to a customers order if the above requirements are met. The promo may be removed from the cart manually."
            error={getFieldError(form, 'restrictions.automatically_apply')}
          >
            <Switch
              id="restrictions.automatically_apply"
              checked={field.value}
              onChange={e =>
                form.setFieldValue(
                  'restrictions.automatically_apply',
                  e.target.checked
                )
              }
            />
          </FormGroup>
        )}
      </FastField>
    </div>
  );
};
